import { FormResult, FormSubmitResult } from "src/components/common/forms/types.ts";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { DeepPartial } from "react-hook-form";
import { PartConfigurationTemplateEditApi } from "src/api/generated/erp/configurator/management/configurationTemplates/partConfigurationTemplateEditApi.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { concatWithPipe } from "src/utils/strings.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { CenteredTypography } from "src/components/common/CenteredTypography.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import i18n from "i18next";
import { PartConfigurationTemplate } from "src/api/generated/erp/db/types/tables/partConfigurationTemplate.ts";
import { PartConfigurationTemplateEditor } from "src/components/views/erp/configurator/managing/salesPartConfigurationTemplates/PartConfigurationTemplateEditor.tsx";
import { PartRevisionView } from "src/api/generated/erp/db/types/tables/partRevisionView.ts";
import { getPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/partRevisionState.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface AddConfigurationTemplateToSalesPartFormProps {
	salesPartId: number;
	closeDialog: () => Promise<unknown>;
	refreshTemplatesDataGrid: () => Promise<unknown>;
}

interface FormValues {
	partRevisionId: number;
}

export const AddConfigurationTemplateToSalesPartForm = (props: AddConfigurationTemplateToSalesPartFormProps) => {
	const { salesPartId, closeDialog: closeDialogSelf, refreshTemplatesDataGrid } = props;

	const { openDialog } = useGenericDialog();

	return (
		<AsyncForm
			fetch={() =>
				PartConfigurationTemplateEditApi.getPartRevisionsWithoutConfigurationTemplate({
					salesPartId,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submit}
			render={(contentParams) => <FormContent {...props} {...contentParams} />}
			onCompleted={onCompleted}
			submitLabel={i18n.t("ok")}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.addConfigurationTemplateToSalesPart.form"),
				data: { salesPartId },
			}}
		/>
	);

	function getDefaultValues(partRevisionOptions: PartRevisionView[]): DeepPartial<FormValues> {
		return {
			partRevisionId: partRevisionOptions[0]?.partRevisionId,
		};
	}

	async function submit(values: FormValues): Promise<FormSubmitResult<PartConfigurationTemplate>> {
		if (!values.partRevisionId) return "interrupted";
		return await PartConfigurationTemplateEditApi.insert({
			salesPartId: salesPartId,
			partRevisionId: values.partRevisionId,
		});
	}

	async function onCompleted(result: FormResult<PartConfigurationTemplate>) {
		switch (result.type) {
			case "cancel":
				await closeDialogSelf();
				break;
			case "success":
				openDialog(({ closeDialog }) => ({
					title: i18n.t("configuration_template"),
					agentContext: {
						key: defineAgentUiContextKey("erp.configurator.configurationTemplate.dialog"),
						title: i18n.t("configuration_template"),
						data: {
							salesPartId,
							partConfigurationTemplateId: result.value.partConfigurationTemplateId,
							partRevisionId: result.value.partRevisionId,
						},
					},
					content: (
						<PartConfigurationTemplateEditor
							partConfigurationTemplate={result.value}
							closeDialog={closeDialog}
							refreshTemplates={refreshTemplatesDataGrid}
						/>
					),
				}));
				await closeDialogSelf();
				break;
		}
	}
};

interface FormContentProps
	extends AddConfigurationTemplateToSalesPartFormProps,
		AsyncFormContentParams<PartRevisionView[], FormValues> {}

const FormContent = ({ control, data: partRevisionOptions }: FormContentProps) => {
	if (partRevisionOptions.length === 0) {
		return <CenteredTypography>{i18n.t("all_part_revisions_already_have_template_configuration")}</CenteredTypography>;
	}

	return (
		<>
			<FormSelectField
				control={control}
				name={"partRevisionId"}
				label={i18n.t("part_revision")}
				options={partRevisionOptions}
				getOptionKey={(o: PartRevisionView) => o.partRevisionId}
				getOptionLabel={(o) =>
					concatWithPipe(o.revisionNumber, getPartRevisionStateLabel(o.partRevisionState), o.note)
				}
				rules={requireRule()}
			/>
		</>
	);
};
